import { createContext, FC, PropsWithChildren, useContext } from 'react';
import { Firm, FirmClient } from '../generated/graphql';
import { getVisibleScorecardElementSymbols } from './elementCustomization';
import { SCORECARD_ELEMENT_SYMBOLS } from './constants';
import { ScorecardElementSymbol } from './util';
import { useFeatureFlagsContext } from '../FeatureFlags';

type ScorecardElementCustomizationContextValue = {
  firm?: Pick<Firm, 'elementCustomizationBetaEnabled' | 'elementCustomizations'> | null;
  firmClient?: Pick<FirmClient, 'elementVisibilityOverrides'> | null;
  isFirmLoading?: boolean;
};

const ScorecardElementCustomizationContext = createContext<ScorecardElementCustomizationContextValue>({
  firm: null,
  firmClient: null,
  isFirmLoading: false,
});

export const ScorecardElementCustomizationProvider: FC<
  PropsWithChildren<ScorecardElementCustomizationContextValue>
> = ({ children, firm = null, firmClient = null, isFirmLoading = false }) => {
  return (
    <ScorecardElementCustomizationContext.Provider value={{ firm, firmClient, isFirmLoading }}>
      {children}
    </ScorecardElementCustomizationContext.Provider>
  );
};

export function useScorecardElementCustomization() {
  return useContext(ScorecardElementCustomizationContext);
}

export function useVisibleScorecardElements(symbols: ScorecardElementSymbol[] = SCORECARD_ELEMENT_SYMBOLS) {
  const { firm, firmClient, isFirmLoading } = useScorecardElementCustomization();
  const { allowHouseholdElementVisibility } = useFeatureFlagsContext();

  if (isFirmLoading && !firm) return [];

  if (firm?.elementCustomizationBetaEnabled !== true) return symbols;

  return getVisibleScorecardElementSymbols({
    allowFirmClientVisibilityOverrides: allowHouseholdElementVisibility === true,
    firm,
    firmClient,
    symbols,
  });
}
